import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Switch,
  FormControl,
  FormLabel,
  FormHelperText,
  VStack,
  HStack,
  Button,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { FaSyncAlt, FaSave } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { updateMySettings } from '../services/authService';

export default function AccountSettings() {
  const { user, updateUser } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.200');

  const [importSyncStockToGateways, setImportSyncStockToGateways] = useState(true);
  const [priceSyncEnabled, setPriceSyncEnabled] = useState(false);
  const [strictCodingMatch, setStrictCodingMatch] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setImportSyncStockToGateways(user?.settings?.importSyncStockToGateways !== false);
    setPriceSyncEnabled(user?.settings?.priceSyncEnabled === true);
    setStrictCodingMatch(user?.settings?.strictCodingMatch === true);
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateMySettings({
        importSyncStockToGateways,
        priceSyncEnabled,
        strictCodingMatch,
      });
      const next = res?.data;
      if (next) {
        const token = localStorage.getItem('token');
        updateUser(token ? { ...next, token } : next);
      }
      toast.success(res?.message || 'تنظیمات ذخیره شد');
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Flex align="center" gap={3} mb={6}>
        <Flex
          w="44px"
          h="44px"
          borderRadius="xl"
          bg="blue.50"
          color="blue.500"
          align="center"
          justify="center"
        >
          <FaSyncAlt />
        </Flex>
        <Box>
          <Heading size="md" color={textColor}>
            تنظیمات همگام‌سازی
          </Heading>
          <Text fontSize="sm" color={mutedColor}>
            رفتار موجودی و قیمت برای حساب شما
          </Text>
        </Box>
      </Flex>

      <Box
        bg={cardBg}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        p={{ base: 5, md: 8 }}
        maxW="640px"
      >
        <VStack spacing={6} align="stretch">
          <FormControl display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box pe={4}>
              <FormLabel mb={1} color={textColor}>
                همگام‌سازی موجودی با درگاه‌ها
              </FormLabel>
              <FormHelperText mt={0} color={mutedColor}>
                با فعال بودن، پس از تغییر موجودی، موجودی به درگاه‌های فروش ارسال می‌شود.
              </FormHelperText>
            </Box>
            <Switch
              colorScheme="blue"
              isChecked={importSyncStockToGateways}
              onChange={(e) => setImportSyncStockToGateways(e.target.checked)}
              mt={1}
            />
          </FormControl>

          <Divider borderColor={borderColor} />

          <FormControl display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box pe={4}>
              <FormLabel mb={1} color={textColor}>
                همگام‌سازی قیمت با درگاه‌ها
              </FormLabel>
              <FormHelperText mt={0} color={mutedColor}>
                با فعال بودن، قیمت تنوع‌های درگاه بر اساس قیمت پایه و درصد تنظیم‌شده محاسبه و ارسال می‌شود.
              </FormHelperText>
            </Box>
            <Switch
              colorScheme="blue"
              isChecked={priceSyncEnabled}
              onChange={(e) => setPriceSyncEnabled(e.target.checked)}
              mt={1}
            />
          </FormControl>

          <Divider borderColor={borderColor} />

          <FormControl display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box pe={4}>
              <FormLabel mb={1} color={textColor}>
                فقط کدهای ثبت‌شده در کدینگ
              </FormLabel>
              <FormHelperText mt={0} color={mutedColor}>
                با فعال بودن، هنگام مرج یا افزودن کالا و تنوع منطقی، فقط کدهایی که در درخت کدینگ هستند پذیرفته می‌شوند؛ بقیه رد می‌شوند.
              </FormHelperText>
            </Box>
            <Switch
              colorScheme="orange"
              isChecked={strictCodingMatch}
              onChange={(e) => setStrictCodingMatch(e.target.checked)}
              mt={1}
            />
          </FormControl>

          <HStack justify="flex-end" pt={2}>
            <Button
              leftIcon={<FaSave />}
              colorScheme="blue"
              borderRadius="xl"
              onClick={handleSave}
              isLoading={saving}
            >
              ذخیره
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
}
